import { useContext, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "@/reducers/hooks"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { selectAccountChats } from "@/reducers/accountChatsSlice"
import { selectArchivedChats } from "@/reducers/chatsSettingsSelectors"
import ChatHelpers from "@/lib/chatHelpers"
import { XmppContext } from "@/stream/xmppClient"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { Chat, Participant } from "@/reducers/chatsSlice"
import { AccountChat } from "@/lib/accountChatsParser"
import { chatGroupLabel } from "@/components/ui/chat-list-group-header"

export type SelectedChat = {
  chatJid: string
  chatName: string
  avatarSrc?: string
  avatarColor: string
  groupName: string
}

type ChatSelectProps = {
  value?: string
  onValueChange: (selectedChat: SelectedChat | undefined) => void
  placeholder?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const getChatAvatarColor = (
  chat: Chat,
  accountChats: Record<string, AccountChat>,
  isArchived: boolean,
) => {
  const chatColor = ChatHelpers.getChatColor(chat)
  if (chatColor) return chatColor

  // Fallback colors based on chat state (same logic as useChatCardData)
  if (isArchived) return "#cccccc"
  if (accountChats[chat.jid]?.group === "shared_driver") return "#ffecb8"

  return "var(--defaultAvatarColor)"
}

export default function ChatSelect({
  value,
  onValueChange,
  placeholder,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: ChatSelectProps) {
  const { t } = useTranslation()
  const [internalOpen, setInternalOpen] = useState(false)

  // Use external control if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const setOpen =
    externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalOpen
  const { myJid } = useContext(XmppContext)

  // Get chat data similar to ChatList
  const chats = useAppSelector(selectChats)
  const accountChats = useAppSelector(selectAccountChats)
  const archivedChats = useAppSelector(selectArchivedChats)

  // Enhanced chats with metadata (similar to ChatList logic)
  const availableChats = useMemo(() => {
    const DEFAULT_GROUP_NAMES_ORDER = [
      "Groups",
      "Drivers",
      "Shared Drivers",
      "Transics",
      "Whatsapp Contacts",
    ]

    let groupNames = Object.values(accountChats).map((chat) => chat.groupName)
    groupNames = [...new Set(DEFAULT_GROUP_NAMES_ORDER.concat(groupNames))]

    const allChats: Array<{
      jid: string
      name: string
      groupName: string
      isArchived: boolean
      driverParticipant?: Participant
      avatarSrc?: string
      avatarColor: string
    }> = []

    groupNames.forEach((groupName) => {
      // Get existing chats from current chats state
      const existingChats = chats
        .filter((c) => accountChats[c.jid]?.groupName === groupName)
        .filter((c) => !archivedChats.includes(c.jid)) // Exclude archived chats
        .map((chat) => {
          const driverParticipant = ChatHelpers.findDriverParticipant(chat)

          return {
            jid: chat.jid,
            name: chat.name,
            groupName,
            isArchived: archivedChats.includes(chat.jid),
            driverParticipant,
            avatarSrc: driverParticipant
              ? participantPhotoToSrc(driverParticipant.photo)
              : undefined,
            avatarColor: getChatAvatarColor(chat, accountChats, false),
          }
        })

      allChats.push(...existingChats)
    })

    // Sort by group name priority, then by chat name
    const groupPriority = {
      Groups: 0,
      Drivers: 1,
      "Double crew": 2,
      "Shared Drivers": 3,
      "Whatsapp Contacts": 4,
    }

    return allChats.sort((a, b) => {
      const priorityA = (groupPriority as any)[a.groupName] ?? 999
      const priorityB = (groupPriority as any)[b.groupName] ?? 999

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      return a.name.localeCompare(b.name)
    })
  }, [chats, accountChats, archivedChats, myJid])

  const handleSelect = (chatJid: string) => {
    // If the same chat is selected again, clear the selection
    if (chatJid === value) {
      onValueChange(undefined)
      setOpen(false)
      return
    }

    const selectedChat = availableChats.find((chat) => chat.jid === chatJid)

    if (!selectedChat) {
      onValueChange(undefined)
      setOpen(false)
      return
    }

    onValueChange({
      chatJid: selectedChat.jid,
      chatName: selectedChat.name,
      avatarSrc: selectedChat.avatarSrc,
      avatarColor: selectedChat.avatarColor,
      groupName: selectedChat.groupName,
    })
    setOpen(false)
  }

  const selectedChatData = value
    ? availableChats.find((chat) => chat.jid === value)
    : undefined

  // Group chats by their group name for display
  const groupedChats = useMemo(() => {
    const groups: Record<string, typeof availableChats> = {}

    availableChats.forEach((chat) => {
      if (!groups[chat.groupName]) {
        groups[chat.groupName] = []
      }
      groups[chat.groupName].push(chat)
    })

    return groups
  }, [availableChats])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            {selectedChatData && (
              <Avatar className="h-6 w-6">
                {selectedChatData.avatarSrc && (
                  <AvatarImage src={selectedChatData.avatarSrc} />
                )}
                <AvatarFallback
                  style={{ backgroundColor: selectedChatData.avatarColor }}
                  className="text-xs font-medium text-gray-900"
                >
                  {getInitials(selectedChatData.name)}
                </AvatarFallback>
              </Avatar>
            )}
            <span className="truncate">
              {selectedChatData
                ? selectedChatData.name
                : (placeholder ?? t("chat.chatSelect.placeholder"))}
            </span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[1050] w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("chat.chatSelect.search")} />
          <CommandList>
            <CommandEmpty>{t("chat.chatSelect.empty")}</CommandEmpty>
            {Object.entries(groupedChats).map(([groupName, chatsInGroup]) => (
              <CommandGroup
                key={groupName}
                heading={chatGroupLabel(t, groupName)}
              >
                {chatsInGroup.map((chat) => (
                  <CommandItem
                    key={chat.jid}
                    value={`${chat.name} ${chat.groupName}`}
                    onSelect={() => handleSelect(chat.jid)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === chat.jid ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <Avatar className="mr-2 h-6 w-6">
                      {chat.avatarSrc && <AvatarImage src={chat.avatarSrc} />}
                      <AvatarFallback
                        style={{ backgroundColor: chat.avatarColor }}
                        className="text-xs font-medium text-gray-900"
                      >
                        {getInitials(chat.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate">{chat.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
